import { ref } from 'vue';

import { useFischer } from './fischerPlugin';

interface CatalogMedia {
  OutputType?: string | null;
  Type?: string | null;
  Url?: string | null;
  videoId?: string | null;
  altText?: string | null;
}

interface CatalogData {
  Media?: null | CatalogMedia[];
}

export interface MediaDownloadParams {
  type: 'product' | 'family' | 'series' | string;
  id: string;
}

/**
 * Get the catalog service URL based on the current environment
 */
export const getCatalogServiceUrl = (): string => {
  switch (APP_ENVIRONMENT) {
    case 'PROD':
      return 'https://api.fischer.group/catalog/v1/';
    case 'UAT':
      return 'https://api-uat.fischer.group/catalog/v1/';
    default:
      return 'https://api-test.fischer.group/catalog/v1/';
  }
};

export const useCatalog = () => {
  const fischer = useFischer();
  const mediaDownloadLink = ref<string | null>(null);
  const catalogService = getCatalogServiceUrl();

  /**
   * Check if media download is available for a catalog item
   * Sets mediaDownloadLink.value to the URL if available, null otherwise
   *
   * @param params - Object containing type and id of the catalog item
   */
  const checkForMediaDownload = ({ type, id }: MediaDownloadParams): void => {
    const lang =
      fischer.catalogLocale() || (fischer.siteLanguage() || '').replace('-', '_') || 'de_DE';
    const url = `${catalogService}media/${type}/${id}/${fischer.brandId()}/${lang}`;
    mediaDownloadLink.value = url;

    if (IS_SSR) {
      return;
    }
    try {
      // note: not using async to allow SSR without prefetch
      fetch(url, { method: 'HEAD' })
        .then((response) => {
          if (response.status === 204) {
            mediaDownloadLink.value = null;
          }
        })
        .catch((_error) => {
          mediaDownloadLink.value = null;
        });
    } catch (error) {
      console.info('Media download check failed:', error);
      mediaDownloadLink.value = null;
    }
  };

  /**
   * Filter and return thumbnails from catalog media
   * Returns media items that are product pictures, videos, or packaging images
   *
   * @param catalogData - Catalog data containing Media array
   * @returns Array of filtered media items suitable for thumbnails
   */
  const getFamilyThumbnails = (catalogData: CatalogData | null | undefined): CatalogMedia[] => {
    if (!catalogData?.Media) {
      return [];
    }
    return catalogData.Media.filter(
      (media) =>
        (media.OutputType === 'PR' &&
          (media.Type === 'Product Picture' ||
            media.Type === 'Product Category Picture' ||
            media.Type === 'Packaging' ||
            media.Type === 'Application Picture')) ||
        (media.OutputType === 'IS' && media.Type === 'Video'),
    );
  };

  return {
    mediaDownloadLink,
    catalogService,
    checkForMediaDownload,
    getFamilyThumbnails,
  };
};
